import React, { useState } from 'react';
import { 
  Alert, 
  Button, 
  Box, 
  Typography, 
  CircularProgress,
  Collapse,
  IconButton,
  Chip
} from '@mui/material';
import { 
  Payment, 
  Launch, 
  Close,
  CheckCircle
} from '@mui/icons-material';
import { useMutation } from '@apollo/client';
import { CREATE_STRIPE_CONNECT_ACCOUNT, GENERATE_STRIPE_ONBOARDING_URL, REFRESH_STRIPE_ACCOUNT_STATUS } from '../../Graphql/mutations';
import { GET_ONE_CATERER } from '../../Graphql/queries';
import { toggleSnackbar } from '../../ReactiveVariables/index';
import '../../styles/stripe-banner.css';
import {
  canReceiveStripeTransfers,
  getStripeTransferBlockedMessage,
} from './stripeTransferStatus';

export default function StripeBanner({ catererId, currentDetails }) {
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isGeneratingUrl, setIsGeneratingUrl] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [stripeAccountData, setStripeAccountData] = useState(null);

  const stripeDetails = currentDetails?.getOneCaterer || {};
  const stripeAccountId = stripeDetails.stripeAccountId;
  const transfersEnabled = canReceiveStripeTransfers(stripeDetails);
  const blockedMessage = getStripeTransferBlockedMessage(stripeDetails);

  const [createStripeConnectAccount] = useMutation(CREATE_STRIPE_CONNECT_ACCOUNT, {
    refetchQueries: [
      {
        query: GET_ONE_CATERER,
        variables: { 
          getOneCatererId: catererId, 
          agsType: "id" 
        }
      }
    ],
    onCompleted: (data) => {
      if (data.createStripeConnectAccount.error) {
        toggleSnackbar({
          status: true,
          message: data.createStripeConnectAccount.error,
          variant: "error",
        });
      } else {
        setStripeAccountData(data.createStripeConnectAccount);
        toggleSnackbar({
          status: true,
          message: "Stripe account created successfully!",
          variant: "success",
        });
      }
      setIsCreatingAccount(false);
    },
    onError: (error) => {
      console.error("Error creating Stripe account:", error);
      toggleSnackbar({
        status: true,
        message: error.message || "Failed to create Stripe account",
        variant: "error",
      });
      setIsCreatingAccount(false);
    },
  });

  const [generateStripeOnboardingUrl] = useMutation(GENERATE_STRIPE_ONBOARDING_URL, {
    refetchQueries: [
      {
        query: GET_ONE_CATERER,
        variables: { 
          getOneCatererId: catererId, 
          agsType: "id" 
        }
      }
    ],
    onCompleted: (data) => {
      if (data.generateStripeOnboardingUrl.error) {
        toggleSnackbar({
          status: true,
          message: data.generateStripeOnboardingUrl.error,
          variant: "error",
        });
      } else {
        setStripeAccountData(data.generateStripeOnboardingUrl);
        toggleSnackbar({
          status: true,
          message: "Stripe onboarding URL updated successfully!",
          variant: "success",
        });
      }
      setIsGeneratingUrl(false);
    },
    onError: (error) => {
      console.error("Error generating Stripe onboarding URL:", error);
      toggleSnackbar({
        status: true,
        message: error.message || "Failed to generate Stripe onboarding URL",
        variant: "error",
      });
      setIsGeneratingUrl(false);
    },
  });

  const [refreshStripeAccountStatus] = useMutation(REFRESH_STRIPE_ACCOUNT_STATUS, {
    refetchQueries: [
      {
        query: GET_ONE_CATERER,
        variables: { 
          getOneCatererId: catererId, 
          agsType: "id" 
        }
      }
    ],
    onCompleted: (data) => {
      if (data.refreshStripeAccountStatus.error) {
        toggleSnackbar({
          status: true,
          message: data.refreshStripeAccountStatus.error,
          variant: "error",
        });
      } else {
        console.log("✅ Stripe account status refreshed:", data.refreshStripeAccountStatus);
        toggleSnackbar({
          status: true,
          message: "Stripe account status updated!",
          variant: "success",
        });
      }
    },
    onError: (error) => {
      console.error("Error refreshing Stripe account status:", error);
      toggleSnackbar({
        status: true,
        message: error.message || "Failed to refresh Stripe account status",
        variant: "error",
      });
    },
  });

  const handleCreateAccount = () => {
    setIsCreatingAccount(true);
    createStripeConnectAccount({
      variables: { catererId }
    });
  };

  const handleCompleteOnboarding = async () => {
    if (isGeneratingUrl) return;
    
    setIsGeneratingUrl(true);
    
    try {
      console.log("🔍 Checking Stripe account status before generating URL...");
      
      // First, refresh the account status to get the latest from Stripe
      await refreshStripeAccountStatus({
        variables: { catererId }
      });
      
      console.log("🔍 Generating Stripe onboarding URL for catererId:", catererId);
      
      const result = await generateStripeOnboardingUrl({
        variables: { catererId }
      });
      
      console.log("🔍 Result from generateStripeOnboardingUrl:", result);
      
      if (result.data?.generateStripeOnboardingUrl?.onboardingUrl) {
        const url = result.data.generateStripeOnboardingUrl.onboardingUrl;
        console.log("🔍 Opening URL in new tab:", url);
        
        // Open in new tab instead of popup (better for Safari and mobile)
        window.open(url, '_blank');
      } else {
        console.log("❌ No onboarding URL in result:", result);
        toggleSnackbar({
          status: true,
          message: "Failed to generate onboarding URL. Please try again.",
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Error generating onboarding URL:", error);
      toggleSnackbar({
        status: true,
        message: "Failed to generate onboarding URL. Please try again.",
        variant: "error",
      });
    } finally {
      setIsGeneratingUrl(false);
    }
  };

  // Don't show the banner once Stripe explicitly allows incoming transfers.
  if (transfersEnabled) {
    return null;
  }

  // Show success state if account was just created
  if (stripeAccountData && !isCreatingAccount) {
    return (
      <Collapse in={isExpanded}>
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            border: '1px solid #e8f5e8',
            backgroundColor: '#f8fff8'
          }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setIsExpanded(false)}
            >
              <Close fontSize="inherit" />
            </IconButton>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckCircle color="success" />
            <Box>
              <Typography variant="h6" sx={{ mb: 1, color: '#2e7d32' }}>
                Stripe Account Created Successfully!
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#388e3c' }}>
                Your Stripe Connect account has been created. Complete the onboarding process to start accepting payments.
              </Typography>
              <Button
                variant="contained"
                color="success"
                onClick={handleCompleteOnboarding}
                startIcon={<Launch />}
                size="small"
                sx={{ mr: 2 }}
              >
                Complete Stripe Onboarding
              </Button>
              <Button
                variant="outlined"
                color="success"
                onClick={() => setIsExpanded(false)}
                size="small"
              >
                Dismiss
              </Button>
            </Box>
          </Box>
        </Alert>
      </Collapse>
    );
  }

  // Show setup banner if no Stripe account exists
  if (!stripeAccountId) {
    return (
      <Collapse in={isExpanded}>
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            border: '1px solid #fff3e0',
            backgroundColor: '#fffbf0'
          }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setIsExpanded(false)}
            >
              <Close fontSize="inherit" />
            </IconButton>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6" sx={{ color: '#000000' }}>
                  Set Up Payment Processing
                </Typography>
                <Chip 
                  label="Required" 
                  size="small" 
                  sx={{ 
                    background: '#ff9800',
                    color: 'white',
                    fontSize: '0.7rem',
                    height: 20
                  }} 
                />
              </Box>
              
              <Typography variant="body2" sx={{ mb: 2, color: '#000000' }}>
                Complete your Stripe Connect setup to start accepting payments and receiving transfers for your orders.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleCreateAccount}
                  disabled={isCreatingAccount}
                  startIcon={isCreatingAccount ? <CircularProgress size={16} /> : <Payment />}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  {isCreatingAccount ? 'Setting Up...' : 'Set Up Stripe Account'}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => setIsExpanded(false)}
                  size="small"
                >
                  Remind Me Later
                </Button>
              </Box>
              
              {/* Benefits Section */}

            </Box>
          </Box>
        </Alert>
      </Collapse>
    );
  }

  // Show a requirements banner if Stripe has not enabled transfers.
  return (
    <Collapse in={isExpanded}>
      <Alert 
        severity="info"
        sx={{ 
          mb: 3,
          borderRadius: 2,
          border: '1px solid #e3f2fd',
          backgroundColor: '#f8fbff'
        }}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={() => setIsExpanded(false)}
          >
            <Close fontSize="inherit" />
          </IconButton>
        }
      >
        <Box>
          <Typography variant="h6" sx={{ mb: 1, color: '#1976d2' }}>
            Stripe Transfers Pending
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#1565c0' }}>
            {blockedMessage}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCompleteOnboarding}
            disabled={isGeneratingUrl}
            startIcon={isGeneratingUrl ? <CircularProgress size={16} /> : <Launch />}
            size="small"
            sx={{ mr: 2 }}
          >
            {isGeneratingUrl ? 'Generating...' : 'Review Stripe Requirements'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setIsExpanded(false)}
            size="small"
          >
            Dismiss
          </Button>
        </Box>
      </Alert>
    </Collapse>
  );
}
